import { Button } from '@consigli/facade';
import { useProjectId, usePackageId, useUpdateFindingMutation, useMyUser } from '@consigli/hooks';
import { Finding } from '@consigli/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Highlight } from 'react-highlighter-ts';
import { useTranslation } from 'react-i18next';
import {
  HiChevronDown,
  HiChevronUp,
  HiOutlineChatAlt2,
  HiOutlineDocumentText,
} from 'react-icons/hi';
import { IoClose } from 'react-icons/io5';
import { LuCheck, LuMessageSquareShare } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { Card } from '@/components/card';
import { FeatureFlag, useFeatureFlag } from '@/feature-flags';
import { UpdateFindingButtons } from '@/features/document-viewer/fullscreen/finding/update-finding';
import { useAgentChatViewport } from '@/layouts/use-agent-chat-viewport';
import { useFindingMessage } from '@/pages/insight/propchat/add-context/use-finding-message';
import { useAgentChatAttachments } from '@/pages/insight/propchat/use-agent-chat-attachments';
import { useFindingContractor } from '@/pages/offer/use-finding-contractor';
import { CheckableFinding } from '@/util/types';

import { useFindingsContext } from '../findings-context';
import { AddLabelButton } from './add-label-button';

interface FindingsCardProps {
  finding: CheckableFinding;
  itemsOnPage: number;
  onCheckboxClick: (record: Finding) => void;
  selectedCard: boolean;
  onCardClick: () => void;
  onCommentsClick: (event: React.MouseEvent<HTMLElement>) => void;
}

export const FindingsCard = ({
  finding,
  itemsOnPage,
  selectedCard,
  onCardClick,
  onCommentsClick,
}: FindingsCardProps) => {
  const { t } = useTranslation();
  const { searchTerm } = useFindingsContext();
  const contractor = useFindingContractor(finding);
  const findingMessage = useFindingMessage(finding);
  const projectId = useProjectId();
  const packageId = usePackageId();
  const [updateFinding] = useUpdateFindingMutation();
  const { user } = useMyUser();
  const { attachments, addContext } = useAgentChatAttachments();
  // Whether this finding is already on the next question, which the CTA's own
  // glyph reports rather than leaving the click to be guessed at twice.
  const isAttachedToChat = attachments.findings.some((entry) => entry.id === finding.id);
  const isContextEnabled = useFeatureFlag(FeatureFlag.PropChatContext);
  const { isAgentChatOpen, openAgentChat } = useAgentChatViewport();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const refContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const overflows = (domRef: HTMLDivElement | null) =>
      !!domRef && domRef.scrollHeight > domRef.clientHeight;
    setIsTruncated(overflows(contentRef.current) || overflows(refContentRef.current));
  }, [finding.content, finding.refContent]);

  const toggleExpanded = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  }, []);

  const handleRemoveLabel = useCallback(
    async (label: string, e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        const updatedLabels = finding.labels?.filter((l) => l !== label) || [];

        await updateFinding({
          projectId,
          packageId,
          findingId: finding.id,
          data: {
            labels: updatedLabels,
          },
        }).unwrap();

        toast.info(t('toast.label-removed-success'));
      } catch (error) {
        console.log('Error removing label:', error);
        toast.error(t('toast.label-removed-failure'));
      }
    },
    [finding, projectId, packageId, updateFinding, t],
  );

  return (
    <>
      <Card selected={selectedCard} onClick={onCardClick}>
        <div className="flex flex-col md:flex-row gap-3 items-start">
          <UpdateFindingButtons
            className="flex-row md:flex-col justify-center"
            finding={finding}
            itemsOnPage={itemsOnPage}
          />
          <div className="flex flex-col flex-1 min-w-0 w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-xs first-letter:uppercase">
                {t(`dashboard.conflict-title.${finding.conflictType}`)}
              </div>
              {contractor && (
                <span className="inline-flex items-center rounded-full bg-day-light-1 px-2 py-0.5 text-xs font-medium text-day-neutral-dark">
                  {contractor}
                </span>
              )}
            </div>
            <div className="font-semibold py-2">
              {/* @ts-expect-error - As event handlers which don't exist for React are required */}
              <Highlight placeholder="" search={searchTerm}>
                <p>{findingMessage}</p>
              </Highlight>
            </div>
            <div className="flex gap-1">
              <HiOutlineDocumentText fontSize={16}></HiOutlineDocumentText>
              {/* @ts-expect-error - As event handlers which don't exist for React are required */}
              <Highlight placeholder="" search={searchTerm}>
                <p className="text-xs wrap-break-word">
                  {finding.fileName ?? t('findings.project-related-finding')}
                </p>
              </Highlight>
            </div>
            {finding.refFileName && (
              <div className="text-xs flex items-center mt-2">
                <HiOutlineDocumentText fontSize={16} className="mr-1"></HiOutlineDocumentText>
                <span className="text-xs">
                  <p>{finding.refFileName}</p>
                </span>
              </div>
            )}
            <div className="flex mt-2">
              <div className="flex flex-wrap items-center">
                {(finding.labels || []).map((label) => (
                  <div
                    className={`text-xs rounded-sm py-1 px-2 mr-2 mb-1 flex items-center ${
                      selectedCard
                        ? 'bg-night-light-3 text-day-light-5'
                        : 'bg-day-light-1 text-day-neutral-dark'
                    }`}
                    key={label}
                  >
                    <IoClose
                      className={`cursor-pointer mr-1 ${
                        selectedCard
                          ? 'hover:text-night-neutral-dark'
                          : 'hover:text-day-neutral-light'
                      }`}
                      size={12}
                      onClick={(e) => handleRemoveLabel(label, e)}
                    />
                    <span
                      className="max-w-[140px] overflow-hidden whitespace-nowrap text-ellipsis inline-block"
                      title={label}
                    >
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-col flex-1 min-w-0 w-0">
            <div>
              <div className="text-xs pb-2"> {t('findings.extract')}: </div>
              <div
                ref={contentRef}
                className={`${isExpanded ? '' : 'line-clamp-3'} wrap-break-word`}
              >
                {/* @ts-expect-error - As event handlers which don't exist for React are required */}
                <Highlight placeholder="" search={searchTerm}>
                  <q>{finding.content ?? t('findings.no-document-reference')}</q>
                </Highlight>
              </div>
            </div>
            {finding.refContent && (
              <div className="mt-2">
                <div className="text-xs pb-2"> {t('findings.extract')}: </div>
                <div
                  ref={refContentRef}
                  className={`${isExpanded ? '' : 'line-clamp-3'} wrap-break-word`}
                >
                  <q>{finding.refContent}</q>
                </div>
              </div>
            )}
            {(isTruncated || isExpanded) && (
              <button
                type="button"
                onClick={toggleExpanded}
                className={`flex items-center gap-1 text-xs mt-2 self-end ${
                  selectedCard
                    ? 'text-day-neutral-light hover:text-day-disabled hover:cursor-pointer'
                    : 'text-day-neutral-dark hover:text-day-neutral-subtle hover:cursor-pointer'
                }`}
              >
                {isExpanded ? (
                  <>
                    {t('findings.show-less')} <HiChevronUp size={14} />
                  </>
                ) : (
                  <>
                    {t('findings.show-more')} <HiChevronDown size={14} />
                  </>
                )}
              </button>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {/*
             * Attaches this finding to the next question and opens the chat on
             * it, so the attachment is visible rather than silently stored.
             * Sits above Assign label, as the design has it, and only while
             * context is switched on - there is nothing to attach it to
             * otherwise.
             *
             * A tick once it is attached: the chips live in the drawer, which
             * may be closed or scrolled away from this card, so the row itself
             * has to say whether it is already on the question. Still live -
             * attaching twice is a no-op, and the chat reopens on it.
             */}
            {isContextEnabled && (
              <Button
                secondary
                rounded
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                  // The card itself opens the finding, which is not what this asks for.
                  event.stopPropagation();
                  addContext({ documents: [], findings: [finding] });
                  /*
                   * Only when it is not already there. openAgentChat replays the
                   * drawer's entrance by clearing hasAgentChatEntered, which the
                   * floating toggle reads to decide whether to show its close
                   * cross - so calling it on an open chat flips that button back
                   * and forth for no reason.
                   */
                  if (!isAgentChatOpen) openAgentChat();
                }}
                className="px-2 py-1 bg-day-light-4 border rounded-md justify-start w-[160px]"
                icon={isAttachedToChat ? LuCheck : LuMessageSquareShare}
                iconSize={16}
              >
                <span className="font-bold text-sm">
                  {/* Past tense once it is on the question, with the tick: the chips
                      live in a drawer that may be closed or scrolled away from this
                      card, so the row has to say so itself. */}
                  {t(isAttachedToChat ? 'prop-chat.added-to-chat' : 'prop-chat.add-to-chat')}
                </span>
              </Button>
            )}
            {!user.isSuperuser && (
              <Button
                secondary
                rounded
                onClick={onCommentsClick}
                className="px-2 py-1 bg-day-light-4 border rounded-md justify-start w-[160px]"
                icon={HiOutlineChatAlt2}
                iconSize={16}
              >
                <span className="font-bold text-sm">{`${t('findings.comments')} (${finding.comments.length})`}</span>
              </Button>
            )}
            <AddLabelButton findings={[finding]} />
          </div>
        </div>
      </Card>
    </>
  );
};
